/**
 * ============================================================================
 * RK KLAVIYO SIGNUP JAVASCRIPT
 * Formular-Logik, Klaviyo API, Popup-Trigger
 * ============================================================================
 *
 * Powered by René Kress - Crovio
 * www.crovio.de
 *
 * © 2025 All Rights Reserved
 * ============================================================================
 */

(function () {
  'use strict';

  // ─── Konstanten ────────────────────────────────────────────────────────────

  const COOKIE_SUBSCRIBED = 'rk_ks_subscribed';
  const COOKIE_DISMISSED  = 'rk_ks_dismissed';
  const KLAVIYO_API       = 'https://a.klaviyo.com/client/subscriptions/';
  // Gemeldete Anmeldungen, damit dieselbe Adresse nicht zweimal als Lead zählt.
  const SPEICHER_GEMELDET = 'rk_ks_gemeldet';
  // Serverseitiger Weg zur Conversions API — faengt ab, was Adblocker und
  // iOS-Tracking-Schutz dem Browser-Pixel wegnehmen.
  const CAPI_ENDPUNKT = 'https://vanaehlismus-ads.web.app/api/oeffentlich/ereignis';

  // ─── Hilfsfunktionen: Cookies ──────────────────────────────────────────────

  function setCookie(name, value, days) {
    const exp = new Date();
    exp.setDate(exp.getDate() + days);
    document.cookie = `${name}=${value};expires=${exp.toUTCString()};path=/;SameSite=Lax`;
  }

  function getCookie(name) {
    const m = document.cookie.match('(?:^|;)\\s*' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  // ─── Meta Pixel Tracking ───────────────────────────────────────────────────

  /*
   * Die Klaviyo-Client-API antwortet auf jede Anmeldung mit 202 Accepted —
   * auch dann, wenn die Adresse längst auf der Liste steht. Ohne eigene
   * Prüfung meldet das Pixel deshalb bei jedem erneuten Absenden einen neuen
   * Lead, und Meta optimiert auf Leute, die schon Abonnenten sind.
   *
   * Darum merken wir uns hier, welche Adressen wir schon gemeldet haben —
   * als SHA-256-Hash, damit keine lesbare E-Mail im Browser liegt.
   *
   * Grenze: Das wirkt pro Browser. Wer dasselbe Formular auf einem zweiten
   * Gerät absendet, wird erneut gemeldet. Das liesse sich nur serverseitig
   * sauber lösen.
   */

  /*
   * Dieselbe Kennung an beide Wege.
   *
   * Browser-Pixel und Server melden dasselbe Ereignis. Ohne gemeinsame ID
   * macht Meta daraus zwei Leads — das waere schlimmer als die Luecke, die
   * der Serverweg schliessen soll.
   */
  function neueEreignisId() {
    if (window.crypto?.randomUUID) return crypto.randomUUID();
    return 'rk-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  /** Metas eigene Cookies — sie heben die Trefferquote deutlich. */
  function pixelCookie(name) {
    return getCookie(name) || undefined;
  }

  /**
   * Meldet dasselbe Ereignis zusaetzlich ueber den Server.
   *
   * Laeuft bewusst ohne await im Aufrufer: Die Kundin hat ihre Anmeldung
   * laengst hinter sich, sie soll auf nichts warten. Schlaegt es fehl,
   * bleibt es beim Browser-Pixel — gemeldet wird dann einmal statt zweimal,
   * nie null mal.
   */
  function meldeServerseitig(daten) {
    try {
      const rumpf = JSON.stringify(daten);
      // sendBeacon ueberlebt auch einen Seitenwechsel direkt danach.
      if (navigator.sendBeacon) {
        const ok = navigator.sendBeacon(CAPI_ENDPUNKT, new Blob([rumpf], { type: 'application/json' }));
        if (ok) return;
      }
      fetch(CAPI_ENDPUNKT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: rumpf,
        keepalive: true,
      }).catch(function () { /* der Browser-Pixel hat schon gemeldet */ });
    } catch (_) { /* dito */ }
  }

  async function hashEmail(email) {
    const sauber = String(email).trim().toLowerCase();
    if (!window.crypto?.subtle) return sauber;          // Rückfall ohne Web Crypto
    const daten = new TextEncoder().encode(sauber);
    const buf   = await crypto.subtle.digest('SHA-256', daten);
    return Array.from(new Uint8Array(buf))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
  }

  function gemeldeteLesen() {
    try {
      return JSON.parse(localStorage.getItem(SPEICHER_GEMELDET) || '[]');
    } catch (_) {
      return [];
    }
  }

  function gemeldeteMerken(hash) {
    try {
      const liste = gemeldeteLesen();
      if (liste.includes(hash)) return;
      liste.push(hash);
      // Nicht unbegrenzt wachsen lassen.
      localStorage.setItem(SPEICHER_GEMELDET, JSON.stringify(liste.slice(-20)));
    } catch (_) { /* privater Modus: dann eben ohne Gedächtnis */ }
  }

  /**
   * Meldet eine Anmeldung an Meta — aber nur, wenn diese Adresse in diesem
   * Browser noch nicht gemeldet wurde.
   * @returns {Promise<boolean>} true, wenn gemeldet wurde
   */
  async function trackMetaLead(email, eventName, vorname, nachname) {
    const hash = await hashEmail(email);
    if (gemeldeteLesen().includes(hash)) {
      return false;                                     // schon Abonnent
    }

    const name  = eventName || 'Lead';
    const id    = neueEreignisId();

    // Weg 1: der Browser-Pixel, sofern er geladen ist.
    if (typeof fbq !== 'undefined') {
      fbq('track', name, {}, { eventID: id });
    }

    // Weg 2: der Server. Er laeuft auch dann, wenn der Pixel blockiert ist —
    // genau das ist der Gewinn.
    meldeServerseitig({
      event_name: name,
      event_id:   id,
      em:         hash,
      fn:         vorname  ? await hashEmail(vorname)  : undefined,
      ln:         nachname ? await hashEmail(nachname) : undefined,
      fbp:        pixelCookie('_fbp'),
      fbc:        pixelCookie('_fbc'),
      quelle:     location.href,
    });

    gemeldeteMerken(hash);
    return true;
  }

  // ─── Klaviyo Subscribe ─────────────────────────────────────────────────────

  async function subscribeToKlaviyo({ publicKey, listId, email, firstName, lastName }) {
    if (!publicKey || !listId) {
      throw new Error('Klaviyo Public Key oder List ID fehlt. Bitte in den Section-Einstellungen hinterlegen.');
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                first_name: firstName,
                last_name:  lastName,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    const res = await fetch(`${KLAVIYO_API}?company_id=${encodeURIComponent(publicKey)}`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'revision':      '2024-07-15',
      },
      body: JSON.stringify(payload),
    });

    // Klaviyo gibt 202 Accepted zurück bei Erfolg (kein Body)
    if (!res.ok && res.status !== 202) {
      let errMsg = `HTTP ${res.status}`;
      try {
        const errData = await res.json();
        errMsg = errData?.errors?.[0]?.detail || errMsg;
      } catch (_) { /* ignore */ }
      throw new Error(errMsg);
    }

    return true;
  }

  // ─── Formular-Handling ─────────────────────────────────────────────────────

  function initForm(formEl, sectionEl) {
    const submitBtn  = formEl.querySelector('[data-rk-ks-submit]');
    const successEl  = formEl.closest('[data-rk-ks-form], .rk-ks__form-wrap, .rk-ks-popup__content')
                              ?.querySelector('[data-rk-ks-success]')
                       || sectionEl.querySelector('[data-rk-ks-success]');
    const errorEl    = formEl.closest('[data-rk-ks-form], .rk-ks__form-wrap, .rk-ks-popup__content')
                              ?.querySelector('[data-rk-ks-error]')
                       || sectionEl.querySelector('[data-rk-ks-error]');
    const errorMsgEl = errorEl?.querySelector('[data-rk-ks-error-msg]');

    // Suche Success/Error im nächstgelegenen Container
    const wrap = formEl.parentElement;
    const successTarget = wrap?.querySelector('[data-rk-ks-success]');
    const errorTarget   = wrap?.querySelector('[data-rk-ks-error]');
    const errorMsgTarget= errorTarget?.querySelector('[data-rk-ks-error-msg]');

    const publicKey = sectionEl.dataset.klaviyoKey;
    const listId    = sectionEl.dataset.klaviyoList;
    const successAction   = sectionEl.dataset.successAction   || 'message';
    const successRedirect = sectionEl.dataset.successRedirect || '';
    const metaEvent       = sectionEl.dataset.metaEvent       || 'Lead';

    if (!submitBtn) return;

    submitBtn.addEventListener('click', async function () {
      // Felder auslesen
      const prefix      = formEl.dataset.prefix || '';
      const firstEl     = formEl.querySelector(`#${prefix}firstname`);
      const lastEl      = formEl.querySelector(`#${prefix}lastname`);
      const emailEl     = formEl.querySelector(`#${prefix}email`);
      const privacyEl   = formEl.querySelector('[name="privacy"]');
      const privacyLabel= formEl.querySelector('.rk-ks__privacy-label');

      // Validierung
      let valid = true;

      [firstEl, lastEl, emailEl].forEach(el => {
        if (!el) return;
        el.classList.remove('rk-ks--invalid');
        if (!el.value.trim() || (el.type === 'email' && !isValidEmail(el.value))) {
          el.classList.add('rk-ks--invalid');
          valid = false;
        }
      });

      if (privacyEl && !privacyEl.checked) {
        privacyLabel?.classList.add('rk-ks--invalid');
        valid = false;
      } else {
        privacyLabel?.classList.remove('rk-ks--invalid');
      }

      if (!valid) {
        // Shake the form
        formEl.style.animation = 'none';
        formEl.offsetHeight; // reflow
        formEl.style.animation = '';
        return;
      }

      // Loading state
      setLoading(submitBtn, true);
      hideEl(errorTarget || errorEl);

      const email = emailEl?.value.trim() || '';

      try {
        await subscribeToKlaviyo({
          publicKey,
          listId,
          email,
          firstName: firstEl?.value.trim()  || '',
          lastName:  lastEl?.value.trim()   || '',
        });

        // Erfolg
        setCookie(COOKIE_SUBSCRIBED, '1', 365);

        // ── Meta Pixel: nur bei einer neuen Adresse melden ─────────────────
        await trackMetaLead(
          email,
          metaEvent,
          firstEl?.value.trim() || '',
          lastEl?.value.trim()  || ''
        );
        // ──────────────────────────────────────────────────────────────────

        handleSuccess({
          formEl,
          successTarget: successTarget || successEl,
          successAction,
          successRedirect,
        });

      } catch (err) {
        console.error('[RK Klaviyo Signup]', err);
        const msgEl = errorMsgTarget || errorMsgEl;
        if (msgEl) msgEl.textContent = err.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.';
        showEl(errorTarget || errorEl);
        setLoading(submitBtn, false);
      }
    });

    // Inline-Validierung: Fehlerklasse entfernen beim Tippen
    formEl.querySelectorAll('.rk-ks__input').forEach(input => {
      input.addEventListener('input', () => input.classList.remove('rk-ks--invalid'));
    });
  }

  function handleSuccess({ formEl, successTarget, successAction, successRedirect }) {
    if (successAction === 'redirect' && successRedirect) {
      window.location.href = successRedirect;
      return;
    }

    hideEl(formEl);
    showEl(successTarget);

    if (successAction === 'both' && successRedirect) {
      setTimeout(() => { window.location.href = successRedirect; }, 2500);
    }
  }

  function setLoading(btn, state) {
    btn.disabled = state;
    btn.classList.toggle('rk-ks__btn--loading', state);
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  function showEl(el) {
    if (!el) return;
    el.removeAttribute('hidden');
    el.style.display = '';
  }

  function hideEl(el) {
    if (!el) return;
    el.setAttribute('hidden', '');
  }

  // ─── Popup ─────────────────────────────────────────────────────────────────

  function initPopup(sectionEl) {
    const popupEnabled = sectionEl.dataset.popupEnabled === 'true';
    if (!popupEnabled) return;

    const popup = document.querySelector('[data-rk-ks-popup]');
    if (!popup) return;

    // Nicht anzeigen wenn schon subscribed oder Cooldown aktiv
    if (getCookie(COOKIE_SUBSCRIBED)) return;
    if (getCookie(COOKIE_DISMISSED)) return;

    // Seitentyp-Check
    const currentPage   = sectionEl.dataset.currentPage || 'other';
    const allowedPages  = (sectionEl.dataset.popupPages || '').split(',').filter(Boolean);

    if (allowedPages.length && !allowedPages.includes('all') && !allowedPages.includes(currentPage)) {
      return; // Diese Seite ist nicht in der Whitelist
    }

    const triggers        = (sectionEl.dataset.popupTriggers || '').split(',').filter(Boolean);
    const timerDelay      = parseInt(sectionEl.dataset.popupDelay   || '30', 10)  * 1000;
    const scrollPercent   = parseInt(sectionEl.dataset.popupScroll  || '50', 10);
    const inactiveSeconds = parseInt(sectionEl.dataset.popupInactive|| '60', 10)  * 1000;
    const cooldownDays    = parseInt(sectionEl.dataset.popupCooldown || '7',  10);

    let shown = false;

    function showPopup() {
      if (shown) return;
      if (getCookie(COOKIE_SUBSCRIBED) || getCookie(COOKIE_DISMISSED)) return;
      shown = true;
      popup.removeAttribute('hidden');
      popup.classList.add('rk-ks-popup--visible');
      document.body.style.overflow = 'hidden';
      // Fokus auf erstes Formularfeld
      setTimeout(() => {
        popup.querySelector('.rk-ks__input')?.focus();
      }, 400);
    }

    function closePopup() {
      if (cooldownDays > 0) {
        setCookie(COOKIE_DISMISSED, '1', cooldownDays);
      }
      popup.classList.add('rk-ks-popup--closing');
      setTimeout(() => {
        popup.setAttribute('hidden', '');
        popup.classList.remove('rk-ks-popup--visible', 'rk-ks-popup--closing');
        document.body.style.overflow = '';
      }, 280);
    }

    // ── Trigger: Exit Intent ─────────────────────────────────────────────────
    if (triggers.includes('exit')) {
      document.addEventListener('mouseleave', function onMouseLeave(e) {
        if (e.clientY <= 10) {
          showPopup();
          // Nur einmal auslösen
          document.removeEventListener('mouseleave', onMouseLeave);
        }
      });

      // Mobile: Back-Button-Intention (pagehide)
      window.addEventListener('pagehide', function () {
        if (!shown) showPopup();
      }, { once: true });
    }

    // ── Trigger: Timer ──────────────────────────────────────────────────────
    if (triggers.includes('timer')) {
      setTimeout(showPopup, timerDelay);
    }

    // ── Trigger: Scroll ─────────────────────────────────────────────────────
    if (triggers.includes('scroll')) {
      function onScroll() {
        const scrolled = window.scrollY + window.innerHeight;
        const total    = document.documentElement.scrollHeight;
        const pct      = (scrolled / total) * 100;
        if (pct >= scrollPercent) {
          showPopup();
          window.removeEventListener('scroll', onScroll, { passive: true });
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // ── Trigger: Inactive ───────────────────────────────────────────────────
    if (triggers.includes('inactive')) {
      let inactiveTimer;

      function resetTimer() {
        clearTimeout(inactiveTimer);
        inactiveTimer = setTimeout(() => {
          showPopup();
          // Nach Anzeige nicht mehr resetten
          ['mousemove', 'keydown', 'touchstart', 'scroll'].forEach(ev => {
            document.removeEventListener(ev, resetTimer, { passive: true });
          });
        }, inactiveSeconds);
      }

      ['mousemove', 'keydown', 'touchstart', 'scroll'].forEach(ev => {
        document.addEventListener(ev, resetTimer, { passive: true });
      });

      // Direkt starten
      resetTimer();
    }

    // ── Popup Schließen: Close-Button & Backdrop ────────────────────────────
    popup.querySelectorAll('[data-rk-ks-popup-close], [data-rk-ks-popup-backdrop]').forEach(el => {
      el.addEventListener('click', closePopup);
    });

    // Escape-Taste
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.hasAttribute('hidden')) {
        closePopup();
      }
    });

    // Popup-Form initialisieren
    popup.querySelectorAll('[data-rk-ks-form]').forEach(f => initForm(f, sectionEl));

    // Nach erfolgreicher Anmeldung auch Popup schließen
    const popupSuccess = popup.querySelector('[data-rk-ks-success]');
    if (popupSuccess) {
      const observer = new MutationObserver(() => {
        if (!popupSuccess.hasAttribute('hidden')) {
          setTimeout(closePopup, 2800);
          observer.disconnect();
        }
      });
      observer.observe(popupSuccess, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function init() {
    const sectionEl = document.querySelector('[data-rk-ks]');
    if (!sectionEl) return;

    // Inline-Formulare
    sectionEl.querySelectorAll('[data-rk-ks-form]').forEach(f => initForm(f, sectionEl));

    // Popup
    initPopup(sectionEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  window.RKKlaviyoSignup = {
    /** Popup manuell öffnen */
    openPopup() {
      document.querySelector('[data-rk-ks-popup]')?.removeAttribute('hidden');
    },
    /** Abonniert-Cookie und gemeldete Adressen löschen (z.B. für Testing) */
    resetCookies() {
      setCookie(COOKIE_SUBSCRIBED, '', -1);
      setCookie(COOKIE_DISMISSED,  '', -1);
      try { localStorage.removeItem(SPEICHER_GEMELDET); } catch (_) {}
      console.info('[RK Klaviyo Signup] Cookies und gemeldete Adressen zurückgesetzt.');
    },

    /** Zeigt, wie viele Adressen in diesem Browser schon gemeldet wurden */
    gemeldete() {
      return gemeldeteLesen().length;
    },
  };

})();